import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, BarChart3, Lightbulb, Target, Shield, Zap, ArrowRight, CheckCircle, Users, FileText, PieChart, TrendingDown } from 'lucide-react';
import { Button } from '../components/ui/button';

export const ApproachPage = () => {
  const principles = [
    {
      icon: Lightbulb,
      title: 'Simplicity over noise',
      description: 'Complex does not mean sophisticated. We build models you can actually use, not spreadsheets that intimidate.'
    },
    {
      icon: Target,
      title: 'Clarity over complexity',
      description: 'Every number tells a story. We make sure you understand yours without needing a finance degree.'
    },
    {
      icon: FileText,
      title: 'Insight over spreadsheets',
      description: 'Data without context is noise. We deliver the why, not just the what.'
    },
    {
      icon: Shield,
      title: 'Value over billing',
      description: 'We solve first, charge later. If our free tools help you today, that is already a win.'
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Listen & Understand',
      description: 'We start by understanding your business reality, pressure points, and the decisions you struggle with.',
      icon: Users,
      color: 'from-[#C9A64A] to-[#D4B76A]'
    },
    {
      number: '02',
      title: 'Diagnose & Design',
      description: 'We identify gaps in your current systems and design frameworks crafted for your specific needs.',
      icon: Target,
      color: 'from-[#0F1A2A] to-[#1A1A1C]'
    },
    {
      number: '03',
      title: 'Build & Implement',
      description: 'We create models that are easy to understand, easy to maintain, and strong enough to guide bold decisions.',
      icon: PieChart,
      color: 'from-[#C9A64A] to-[#D4B76A]'
    },
    {
      number: '04',
      title: 'Evolve & Support',
      description: 'Systems that grow with you. We do not believe in one-time files that lose relevance.',
      icon: TrendingUp,
      color: 'from-[#0F1A2A] to-[#1A1A1C]'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-6 py-6 bg-white/80 backdrop-blur-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="relative">
              <TrendingUp className="h-7 w-7 text-[#C9A64A]" />
              <BarChart3 className="h-4 w-4 text-[#C9A64A] absolute -bottom-1 -right-1" />
            </div>
            <span className="text-xl font-bold text-[#1A1A1C] tracking-tight">Virtual<span className="text-[#C9A64A]">CFO</span></span>
          </Link>
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-b from-[#0F1A2A] to-[#1A1A1C] text-white relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-96 h-96 bg-[#C9A64A] opacity-10 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-[#5A6270] opacity-10 rounded-full blur-3xl animate-float-delayed" />
        </div>

        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold mb-8 leading-tight">
              Our Approach
            </h1>
            <div className="w-32 h-1 bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] mx-auto rounded-full" />
          </div>

          <div className="space-y-8 text-xl md:text-2xl font-light leading-relaxed">
            <p>
              We believe finance works best when it feels <span className="font-semibold text-[#C9A64A]">simple, structured, and honest</span>.
            </p>
            <p>
              Our role as a Virtual CFO is not to overwhelm you with more data or files, but to bring clarity, control, and meaning to what is already in front of you.
            </p>
            <p className="text-gray-300">
              We begin by listening.<br />
              Understanding your business, your pressure points, and the decisions you struggle with.<br />
              Only then do we build. Not templates, but frameworks crafted for your reality.
            </p>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="glass-card p-12 md:p-16 rounded-3xl">
            <div className="space-y-8 text-xl md:text-2xl text-[#1A1A1C] font-light leading-relaxed">
              <p>
                Every model we design must be <span className="font-semibold">easy to understand, easy to maintain, and strong enough to guide bold decisions</span>.
              </p>
              <p>
                We do not believe in one-time files that lose relevance. We believe in systems that evolve as you grow.
              </p>
              <p className="text-[#5A6270]">
                We do not chase volume or scale for the sake of it.<br />
                We prefer fewer partnerships where we can think deeply, work closely, and deliver outcomes that matter.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Flowchart */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#F8F8F8] to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A1A1C] mb-4">How We Work</h2>
            <p className="text-xl text-[#5A6270] font-light">A simple, structured approach to financial clarity</p>
          </div>

          {/* Flowchart */}
          <div className="relative">
            {/* Connection Lines */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-[#C9A64A] via-[#5A6270] to-[#C9A64A] opacity-20 -translate-y-1/2" />
            
            <div className="grid md:grid-cols-4 gap-8 relative z-10">
              {processSteps.map((step, index) => (
                <div key={index} className="text-center">
                  {/* Step Card */}
                  <div className={`glass-card p-8 rounded-2xl border-2 border-[#C9A64A]/20 hover:border-[#C9A64A] transition-all duration-300 hover:shadow-xl group relative overflow-hidden`}>
                    {/* Background Gradient */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${step.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                    
                    {/* Number Badge */}
                    <div className="relative z-10">
                      <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] flex items-center justify-center shadow-lg">
                        <span className="text-2xl font-bold text-[#0F1A2A]">{step.number}</span>
                      </div>
                      
                      {/* Icon */}
                      <div className="mb-6">
                        <step.icon className="h-12 w-12 mx-auto text-[#C9A64A]" />
                      </div>
                      
                      {/* Content */}
                      <h3 className="text-xl font-bold text-[#1A1A1C] mb-4">{step.title}</h3>
                      <p className="text-[#5A6270] leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                  
                  {/* Arrow */}
                  {index < processSteps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/2">
                      <ArrowRight className="h-8 w-8 text-[#C9A64A]" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Principles Grid */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A1A1C] mb-4">Our Quiet Principles</h2>
            <p className="text-xl text-[#5A6270] font-light">What guides every decision we make</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {principles.map((principle, index) => (
              <div key={index} className="glass-card p-10 rounded-2xl hover:shadow-xl transition-all duration-300 group border border-gray-200 hover:border-[#C9A64A]">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-gradient-to-br from-[#C9A64A]/10 to-transparent flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <principle.icon className="h-8 w-8 text-[#C9A64A]" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-[#1A1A1C] mb-3">{principle.title}</h3>
                    <p className="text-[#5A6270] leading-relaxed text-lg">{principle.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value-First Philosophy */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#F8F8F8] to-white">
        <div className="max-w-5xl mx-auto">
          <div className="glass-card p-12 md:p-16 rounded-3xl border-2 border-[#C9A64A]/30">
            <div className="text-center mb-12">
              <Zap className="h-16 w-16 mx-auto text-[#C9A64A] mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1C] mb-6">
                We Aim to Solve Before We Charge
              </h2>
            </div>
            
            <div className="space-y-6 text-xl text-[#1A1A1C] font-light leading-relaxed">
              <p>
                If our <Link to="/calculators" className="text-[#C9A64A] font-semibold hover:underline">free calculators</Link> or <Link to="/articles" className="text-[#C9A64A] font-semibold hover:underline">sample work</Link> can help you today, we consider that a win.
              </p>
              <p className="text-center text-2xl font-semibold text-[#C9A64A]">
                Value earned first. Fees only when trust is built and progress is visible.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Decision Framework */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A1A1C] mb-4">Finance as Confidence</h2>
            <p className="text-xl text-[#5A6270] font-light">From chaos to clarity in four stages</p>
          </div>

          {/* Visual Journey */}
          <div className="relative">
            <div className="grid md:grid-cols-4 gap-8">
              {/* Stage 1: Chaos */}
              <div className="text-center">
                <div className="mb-6 relative">
                  <div className="w-32 h-32 mx-auto rounded-2xl bg-gradient-to-br from-red-500/20 to-red-600/10 flex items-center justify-center border-2 border-red-500/30">
                    <TrendingDown className="h-16 w-16 text-red-500" />
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-12 h-12 rounded-full bg-red-500 text-white flex items-center justify-center font-bold shadow-lg">
                    1
                  </div>
                </div>
                <h3 className="text-xl font-bold text-[#1A1A1C] mb-2">Financial Chaos</h3>
                <p className="text-[#5A6270]">Multiple spreadsheets, unclear metrics, reactive decisions</p>
              </div>

              {/* Stage 2: Structure */}
              <div className="text-center">
                <div className="mb-6 relative">
                  <div className="w-32 h-32 mx-auto rounded-2xl bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 flex items-center justify-center border-2 border-yellow-500/30">
                    <FileText className="h-16 w-16 text-yellow-600" />
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-12 h-12 rounded-full bg-yellow-600 text-white flex items-center justify-center font-bold shadow-lg">
                    2
                  </div>
                </div>
                <h3 className="text-xl font-bold text-[#1A1A1C] mb-2">Basic Structure</h3>
                <p className="text-[#5A6270]">Organized data, consistent reporting, beginning to understand</p>
              </div>

              {/* Stage 3: Insight */}
              <div className="text-center">
                <div className="mb-6 relative">
                  <div className="w-32 h-32 mx-auto rounded-2xl bg-gradient-to-br from-blue-500/20 to-blue-600/10 flex items-center justify-center border-2 border-blue-500/30">
                    <Target className="h-16 w-16 text-blue-600" />
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold shadow-lg">
                    3
                  </div>
                </div>
                <h3 className="text-xl font-bold text-[#1A1A1C] mb-2">Strategic Insight</h3>
                <p className="text-[#5A6270]">Driver-based models, scenario planning, informed decisions</p>
              </div>

              {/* Stage 4: Confidence */}
              <div className="text-center">
                <div className="mb-6 relative">
                  <div className="w-32 h-32 mx-auto rounded-2xl bg-gradient-to-br from-[#C9A64A]/20 to-[#D4B76A]/10 flex items-center justify-center border-2 border-[#C9A64A]/30">
                    <CheckCircle className="h-16 w-16 text-[#C9A64A]" />
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-12 h-12 rounded-full bg-[#C9A64A] text-[#0F1A2A] flex items-center justify-center font-bold shadow-lg">
                    4
                  </div>
                </div>
                <h3 className="text-xl font-bold text-[#1A1A1C] mb-2">Full Confidence</h3>
                <p className="text-[#5A6270]">Making decisions without hesitation, executing with clarity</p>
              </div>
            </div>

            {/* Progress Arrow */}
            <div className="hidden md:block absolute top-16 left-0 right-0 h-1 bg-gradient-to-r from-red-500 via-yellow-500 via-blue-500 to-[#C9A64A] opacity-20" />
          </div>
        </div>
      </section>

      {/* Final Statement */}
      <section className="py-20 px-6 bg-gradient-to-r from-[#0F1A2A] to-[#1A1A1C] text-white">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold leading-tight">
            Finance is not just about forecasting.<br />
            <span className="text-gradient">It is about confidence.</span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-300 font-light leading-relaxed">
            It is about making decisions without hesitation.
          </p>
          <p className="text-xl text-gray-300 font-light max-w-2xl mx-auto leading-relaxed">
            If you want a partner who helps you think better, plan better, execute better, you will feel at home here.
          </p>
          
          <div className="pt-8">
            <Link to="/">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] text-[#0F1A2A] hover:scale-105 transition-all duration-300 px-10 py-6 text-lg font-bold rounded-xl"
              >
                Start a Conversation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
